import React from 'react';
import { motion } from 'framer-motion';
import { Check, X, AlertCircle } from 'lucide-react';
export function IngredientDemo() {
  return <section className="py-20 bg-white overflow-hidden" data-id="element-186">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-187">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-188">
          {/* Left: Text */}
          <div className="order-2 lg:order-1" data-id="element-189">
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-6" data-id="element-190">
              See It In Action
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-191">
              IntelliEats breaks down complex ingredient lists into simple,
              actionable insights. We highlight what's safe, what to limit, and
              what to avoid completely.
            </p>

            <div className="space-y-6" data-id="element-192">
              <div className="flex items-start gap-4" data-id="element-193">
                <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center shrink-0" data-id="element-194">
                  <Check className="w-5 h-5 text-brand-600" data-id="element-195" />
                </div>
                <div data-id="element-196">
                  <h3 className="font-bold text-slate-900 text-lg" data-id="element-197">
                    Safe Ingredients
                  </h3>
                  <p className="text-slate-600" data-id="element-198">
                    Green indicators for natural, healthy ingredients that are
                    safe to consume.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-199">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center shrink-0" data-id="element-200">
                  <AlertCircle className="w-5 h-5 text-accent-600" data-id="element-201" />
                </div>
                <div data-id="element-202">
                  <h3 className="font-bold text-slate-900 text-lg" data-id="element-203">
                    Limited Intake
                  </h3>
                  <p className="text-slate-600" data-id="element-204">
                    Amber warnings for ingredients like sugar or sodium that
                    should be moderated.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-205">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0" data-id="element-206">
                  <X className="w-5 h-5 text-red-600" data-id="element-207" />
                </div>
                <div data-id="element-208">
                  <h3 className="font-bold text-slate-900 text-lg" data-id="element-209">
                    Ingredients to Avoid
                  </h3>
                  <p className="text-slate-600" data-id="element-210">
                    Red alerts for harmful additives, artificial colors, and
                    preservatives.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Visual Demo */}
          <div className="order-1 lg:order-2 relative" data-id="element-211">
            <div className="absolute inset-0 bg-gradient-to-tr from-brand-100 to-accent-100 rounded-3xl transform rotate-3 scale-105 -z-10 opacity-50" data-id="element-212"></div>

            <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden" data-id="element-213">
              {/* Mock Header */}
              <div className="bg-slate-50 border-b border-slate-100 p-4 flex items-center gap-2" data-id="element-214">
                <div className="flex gap-1.5" data-id="element-215">
                  <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-216"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-217"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-218"></div>
                </div>
                <div className="ml-4 text-xs font-medium text-slate-400" data-id="element-219">
                  Analysis Result
                </div>
              </div>

              <div className="p-6 sm:p-8" data-id="element-220">
                <div className="mb-6" data-id="element-221">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-222">
                    Scanned Text
                  </h4>
                  <div className="p-4 bg-slate-50 rounded-xl text-sm text-slate-600 font-mono leading-relaxed border border-slate-100" data-id="element-223">
                    Water, Sugar, Corn Syrup, Artificial Colors (Red 40, Yellow
                    5), Sodium Benzoate, Citric Acid, Natural Flavors
                  </div>
                </div>

                <div data-id="element-224">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-225">
                    AI Detection
                  </h4>
                  <div className="space-y-2" data-id="element-226">
                    <motion.div initial={{
                    width: 0,
                    opacity: 0
                  }} whileInView={{
                    width: '100%',
                    opacity: 1
                  }} transition={{
                    duration: 0.5
                  }} className="flex items-center justify-between p-3 bg-red-50 border border-red-100 rounded-lg" data-id="element-227">
                      <span className="font-medium text-slate-900" data-id="element-228">
                        Red 40, Yellow 5
                      </span>
                      <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded" data-id="element-229">
                        AVOID
                      </span>
                    </motion.div>

                    <motion.div initial={{
                    width: 0,
                    opacity: 0
                  }} whileInView={{
                    width: '100%',
                    opacity: 1
                  }} transition={{
                    duration: 0.5,
                    delay: 0.2
                  }} className="flex items-center justify-between p-3 bg-red-50 border border-red-100 rounded-lg" data-id="element-230">
                      <span className="font-medium text-slate-900" data-id="element-231">
                        Sodium Benzoate
                      </span>
                      <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded" data-id="element-232">
                        AVOID
                      </span>
                    </motion.div>

                    <motion.div initial={{
                    width: 0,
                    opacity: 0
                  }} whileInView={{
                    width: '100%',
                    opacity: 1
                  }} transition={{
                    duration: 0.5,
                    delay: 0.4
                  }} className="flex items-center justify-between p-3 bg-accent-50 border border-accent-100 rounded-lg" data-id="element-233">
                      <span className="font-medium text-slate-900" data-id="element-234">
                        Sugar, Corn Syrup
                      </span>
                      <span className="px-2 py-1 bg-accent-100 text-accent-700 text-xs font-bold rounded" data-id="element-235">
                        LIMIT
                      </span>
                    </motion.div>

                    <motion.div initial={{
                    width: 0,
                    opacity: 0
                  }} whileInView={{
                    width: '100%',
                    opacity: 1
                  }} transition={{
                    duration: 0.5,
                    delay: 0.6
                  }} className="flex items-center justify-between p-3 bg-brand-50 border border-brand-100 rounded-lg" data-id="element-236">
                      <span className="font-medium text-slate-900" data-id="element-237">
                        Water, Natural Flavors
                      </span>
                      <span className="px-2 py-1 bg-brand-100 text-brand-700 text-xs font-bold rounded" data-id="element-238">
                        SAFE
                      </span>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}